import { useState } from "react";
import { ChevronDown, ChevronRight, Eye, FileText, Loader2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useNetsuiteClientById } from "@/hooks/useMasterData";
import { Document } from "@/types/models";
import { formatDataRetention } from "@/lib/formatDataRetention";

interface AddedEvidenceCardProps {
  document: Document;
  onRemove: () => void;
  onView: () => void;
  isRemoving?: boolean;
}

const AddedEvidenceCard = ({ document, onRemove, onView, isRemoving = false }: AddedEvidenceCardProps) => {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const { data: client } = useNetsuiteClientById(
    document.ownership === "Client" && document.clientId ? document.clientId : null
  );

  return (
    <Card className="border-[#037C72]/20 bg-[#037C72]/5">
      <CardContent className="p-4">
      <div className="flex items-start gap-2">
        <FileText className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 w-full">
          <div className="float-right flex items-start">
            {document.year && (
              <Badge variant="brand2" className="">{document.year}</Badge>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="View original PDF"
              onClick={onView}
              className="ml-3 w-7 h-7 text-muted-foreground hover:text-brand-teal hover:bg-brand-teal/10"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Remove from project"
              onClick={onRemove}
              disabled={isRemoving}
              className="w-7 h-7"
            >
              {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            </Button>
          </div>

          <p className="font-semibold text-foreground">{document.title}</p>
          {document.filename && <p className="text-xs text-muted-foreground mt-0.5">
            <span className="font-bold">File:</span> {document.filename}
          </p>}
          {document.author && (
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="font-bold">Author:</span> {document.author}
            </p>
          )}
          {document.description && <p className="text-sm text-muted-foreground mt-1">{document.description}</p>}
          {document.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {document.tags.map((tag) => (
                <Badge key={tag} variant="secondary" appearance="outlined" >{tag}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <Collapsible open={metadataOpen} onOpenChange={setMetadataOpen} className="mt-2">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-brand-teal hover:underline">
          {metadataOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Show Metadata
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 rounded-md border bg-background p-4 space-y-1.5 text-xs text-muted-foreground">
          {document.country && <p><span className="font-semibold text-foreground">Country:</span> {document.country}</p>}
          {document.year && <p><span className="font-semibold text-foreground">Year / Publication date:</span> {document.year}</p>}
          {document.sourceType && <p><span className="font-semibold text-foreground">Source type:</span> {document.sourceType}</p>}
          {document.sourceOrganisation && <p><span className="font-semibold text-foreground">Source organisation:</span> {document.sourceOrganisation}</p>}
          {document.url && (
            <p className="truncate">
              <span className="font-semibold text-foreground">URL / Document link:</span>{" "}
              <a href={document.url} target="_blank" rel="noreferrer" className="text-brand-teal hover:underline">{document.url}</a>
            </p>
          )}
          {document.language && <p><span className="font-semibold text-foreground">Language:</span> {document.language}</p>}
          {document.evidenceType && <p><span className="font-semibold text-foreground">Evidence type:</span> {document.evidenceType}</p>}
          {document.dataRetention && <p><span className="font-semibold text-foreground">Data retention:</span> {formatDataRetention(document.dataRetention)}</p>}
          {document.ownership && (
            <p>
              <span className="font-semibold text-foreground">Ownership / Reusability:</span> {document.ownership}
              {document.ownership === "Client" && client?.name ? ` (${client.name})` : ""}
            </p>
          )}
          {document.filename && <p className="truncate"><span className="font-semibold text-foreground">File:</span> {document.filename}</p>}
        </CollapsibleContent>
      </Collapsible>
      </CardContent>
    </Card>
  );
};

export default AddedEvidenceCard;
